import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Clock, 
  User, 
  Calendar, 
  Stethoscope, 
  FileText, 
  Phone, 
  Mail, 
  MapPin,
  Edit,
  X,
  CheckCircle,
  AlertCircle,
  Receipt,
  CreditCard,
  Printer,
  Check,
  ShoppingBag
} from 'lucide-react';
import Header from '../components/Layout/Header';
import { appointments, patients, doctors } from '../data/mockData';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 100px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 20px;
`;

const StatusBanner = styled.div`
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'Confirmada':
        return theme.colors.success;
      case 'Pendiente':
        return theme.colors.warning;
      case 'Reprogramada':
        return theme.colors.info;
      case 'Cancelada':
        return theme.colors.danger;
      case 'Completada':
        return theme.colors.success;
      default:
        return theme.colors.gray;
    }
  }};
  color: ${({ $status, theme }) => {
    switch ($status) {
      case 'Confirmada':
        return theme.colors.successText;
      case 'Pendiente':
        return theme.colors.warningText;
      case 'Reprogramada':
        return theme.colors.infoText;
      case 'Cancelada':
        return theme.colors.dangerText;
      case 'Completada':
        return theme.colors.successText;
      default:
        return theme.colors.text;
    }
  }};
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: 14px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MainCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const AppointmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TimeBox = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px 20px;
  text-align: center;
  min-width: 90px;
`;

const TimeText = styled.div`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const DateText = styled.div`
  font-size: 12px;
  opacity: 0.9;
  margin-top: 4px;
`;

const AppointmentInfo = styled.div`
  flex: 1;
`;

const AppointmentType = styled.h2`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const AppointmentDuration = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PersonCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const PersonImage = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    width: 28px;
    height: 28px;
    color: ${({ theme }) => theme.colors.white};
  }
`;

const PersonInfo = styled.div`
  flex: 1;
`;

const PersonName = styled.h4`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const PersonDetail = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 10px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const NotesSection = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  margin-top: 16px;
`;

const NotesText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const PrimaryButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const DangerButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.dangerText};

  &:hover {
    opacity: 0.9;
  }
`;

const SuccessButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.success};
  color: ${({ theme }) => theme.colors.successText};

  &:hover {
    opacity: 0.9;
  }
`;

const ServicesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  border: ${({ $selected }) => $selected ? '2px solid' : 'none'};
  border-color: ${({ $selected, theme }) => $selected ? theme.colors.primary : 'transparent'};
  cursor: ${({ $editable }) => $editable ? 'pointer' : 'default'};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme, $editable }) => $editable ? theme.colors.gray : theme.colors.background};
  }
`;

const ServiceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const ServiceCheckbox = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${({ $checked, theme }) => $checked ? theme.colors.primary : theme.colors.border};
  background: ${({ $checked, theme }) => $checked ? theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    color: white;
  }
`;

const ServiceDetails = styled.div`
  flex: 1;
`;

const ServiceName = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;
`;

const ServiceMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ServicePrice = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};
`;

const ServicesTotalBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary}10;
  border-radius: 12px;
  margin-top: 12px;
`;

const TotalLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TotalValue = styled.span`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const EditServicesButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// Servicios disponibles por médico (mismo que en ReservarCita)
const doctorServices = {
  1: [
    { id: 1, name: 'Electrocardiograma Pediátrico', price: 350, duration: 30 },
    { id: 2, name: 'Ecocardiograma Fetal', price: 800, duration: 45 },
    { id: 3, name: 'Consulta de Arritmias', price: 500, duration: 30 },
    { id: 4, name: 'Seguimiento Post-operatorio', price: 400, duration: 30 },
    { id: 5, name: 'Evaluación Pre-deportiva', price: 300, duration: 20 },
  ],
  2: [
    { id: 1, name: 'Consulta General', price: 300, duration: 30 },
    { id: 2, name: 'Vacunación', price: 150, duration: 15 },
    { id: 3, name: 'Control de Niño Sano', price: 350, duration: 40 },
  ],
  3: [
    { id: 1, name: 'Consulta Dermatológica', price: 400, duration: 30 },
    { id: 2, name: 'Tratamiento de Acné', price: 600, duration: 45 },
    { id: 3, name: 'Cirugía Menor', price: 1200, duration: 60 },
  ],
  4: [
    { id: 1, name: 'Examen Visual', price: 250, duration: 20 },
    { id: 2, name: 'Cirugía Láser', price: 5000, duration: 90 },
    { id: 3, name: 'Tratamiento de Cataratas', price: 8000, duration: 120 },
  ],
};

const DetalleCita = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Buscar la cita por ID
  const appointment = appointments.find(apt => apt.id === parseInt(id));
  
  // Buscar información del paciente (si existe)
  const patient = patients.find(p => p.name === appointment?.patient);
  
  // Buscar información del médico (si existe)
  const doctor = doctors.find(d => d.name === appointment?.doctor);
  
  // Estado para edición de servicios
  const [isEditingServices, setIsEditingServices] = useState(false);
  const [selectedServices, setSelectedServices] = useState(appointment?.services || []);
  
  // Obtener servicios disponibles del médico
  const availableServices = doctor ? (doctorServices[doctor.id] || []) : [];
  
  // Calcular totales
  const totalPrice = selectedServices.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);
  
  // Toggle servicio
  const handleServiceToggle = (service) => {
    const isSelected = selectedServices.some(s => s.id === service.id && s.name === service.name);
    if (isSelected) {
      setSelectedServices(selectedServices.filter(s => !(s.id === service.id && s.name === service.name)));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };
  
  // Guardar cambios de servicios
  const handleSaveServices = () => {
    // Aquí iría la lógica para guardar en backend
    setIsEditingServices(false);
  };
  
  // Cancelar edición
  const handleCancelEdit = () => {
    setSelectedServices(appointment?.services || []);
    setIsEditingServices(false);
  };

  if (!appointment) {
    return (
      <PageContainer>
        <Header title="Detalle de Cita" showBack />
        <Content>
          <MainCard>
            <SectionTitle>
              <AlertCircle />
              Cita no encontrada
            </SectionTitle>
            <NotesText>La cita que buscas no existe o ha sido eliminada.</NotesText>
          </MainCard>
        </Content>
      </PageContainer>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Confirmada':
        return <CheckCircle />;
      case 'Pendiente':
        return <Clock />;
      case 'Reprogramada':
        return <Calendar />;
      case 'Cancelada':
        return <X />;
      case 'Completada':
        return <CheckCircle />;
      default:
        return <AlertCircle />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  };

  return (
    <PageContainer>
      <Header title="Detalle de Cita" showBack />
      
      <StatusBanner $status={appointment.status}>
        {getStatusIcon(appointment.status)}
        {appointment.status}
      </StatusBanner>

      <Content>
        {/* Información principal de la cita */}
        <MainCard>
          <AppointmentHeader>
            <TimeBox>
              <TimeText>{appointment.time}</TimeText>
              <DateText>{appointment.duration} min</DateText>
            </TimeBox>
            <AppointmentInfo>
              <AppointmentType>{appointment.type}</AppointmentType>
              <AppointmentDuration>
                <Calendar />
                {formatDate(appointment.date)}
              </AppointmentDuration>
            </AppointmentInfo>
          </AppointmentHeader>

          {/* Información del paciente */}
          <SectionTitle>
            <User />
            Paciente
          </SectionTitle>
          <PersonCard onClick={() => patient && navigate(`/perfil-paciente/${patient.id}`)}>
            <PersonImage>
              {patient?.image ? (
                <img src={patient.image} alt={appointment.patient} />
              ) : (
                <User />
              )}
            </PersonImage>
            <PersonInfo>
              <PersonName>{appointment.patient}</PersonName>
              <PersonDetail>
                {patient ? `ID: ${patient.patientId}` : 'Paciente'}
              </PersonDetail>
            </PersonInfo>
          </PersonCard>

          {patient && (
            <ContactInfo>
              <ContactItem href={`tel:${patient.phone}`}>
                <Phone />
                <span>{patient.phone}</span>
              </ContactItem>
              <ContactItem href={`mailto:${patient.email}`}>
                <Mail />
                <span>{patient.email}</span>
              </ContactItem>
            </ContactInfo>
          )}
        </MainCard>

        {/* Información del médico */}
        <MainCard>
          <SectionTitle>
            <Stethoscope />
            Médico
          </SectionTitle>
          <PersonCard onClick={() => doctor && navigate(`/perfil-medico/${doctor.id}`)}>
            <PersonImage>
              {doctor?.image ? (
                <img src={doctor.image} alt={appointment.doctor} />
              ) : (
                <Stethoscope />
              )}
            </PersonImage>
            <PersonInfo>
              <PersonName>{appointment.doctor}</PersonName>
              <PersonDetail>
                {doctor ? doctor.specialty : 'Especialista'}
              </PersonDetail>
            </PersonInfo>
          </PersonCard>
        </MainCard>

        {/* Servicios */}
        <MainCard>
          <SectionHeader>
            <SectionTitle>
              <ShoppingBag />
              Servicios
            </SectionTitle>
            {appointment.status !== 'Cancelada' && appointment.status !== 'Completada' && (
              <>
                {!isEditingServices ? (
                  <EditServicesButton onClick={() => setIsEditingServices(true)}>
                    <Edit />
                    Editar
                  </EditServicesButton>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <EditServicesButton onClick={handleCancelEdit}>
                      <X />
                      Cancelar
                    </EditServicesButton>
                    <EditServicesButton onClick={handleSaveServices} style={{ background: '#4CAF50', color: 'white', borderColor: '#4CAF50' }}>
                      <Check />
                      Guardar
                    </EditServicesButton>
                  </div>
                )}
              </>
            )}
          </SectionHeader>
          
          <ServicesContainer>
            {isEditingServices ? (
              // Modo edición - mostrar todos los servicios disponibles
              availableServices.map(service => {
                const isSelected = selectedServices.some(s => s.id === service.id && s.name === service.name);
                return (
                  <ServiceItem 
                    key={service.id} 
                    $selected={isSelected}
                    $editable={true}
                    onClick={() => handleServiceToggle(service)}
                  >
                    <ServiceInfo>
                      <ServiceCheckbox $checked={isSelected}>
                        {isSelected && <Check />}
                      </ServiceCheckbox>
                      <ServiceDetails>
                        <ServiceName>{service.name}</ServiceName>
                        <ServiceMeta>
                          <span>{service.duration} min</span>
                          <ServicePrice>${service.price}</ServicePrice>
                        </ServiceMeta>
                      </ServiceDetails>
                    </ServiceInfo>
                  </ServiceItem>
                );
              })
            ) : (
              // Modo vista - mostrar servicios seleccionados
              selectedServices.map((service, index) => (
                <ServiceItem key={index} $editable={false}>
                  <ServiceInfo>
                    <ServiceDetails>
                      <ServiceName>{service.name}</ServiceName>
                      <ServiceMeta>
                        <span>{service.duration} min</span>
                        <ServicePrice>${service.price}</ServicePrice>
                      </ServiceMeta>
                    </ServiceDetails>
                  </ServiceInfo>
                </ServiceItem>
              ))
            )}
          </ServicesContainer>
          
          <ServicesTotalBar>
            <div>
              <TotalLabel>Duración total: </TotalLabel>
              <TotalValue>{totalDuration} min</TotalValue>
            </div>
            <div>
              <TotalLabel>Total: </TotalLabel>
              <TotalValue>${totalPrice}</TotalValue>
            </div>
          </ServicesTotalBar>
        </MainCard>

        {/* Notas */}
        <MainCard>
          <SectionTitle>
            <FileText />
            Notas de la cita
          </SectionTitle>
          <NotesSection>
            <NotesText>
              No hay notas adicionales para esta cita. Las notas se agregarán durante o después de la consulta.
            </NotesText>
          </NotesSection>
        </MainCard>

        {/* Acciones */}
        <MainCard>
          <SectionTitle>Acciones</SectionTitle>
          <ActionsContainer>
            {appointment.status !== 'Cancelada' && appointment.status !== 'Completada' && (
              <>
                <PrimaryButton>
                  <CheckCircle />
                  Confirmar Asistencia
                </PrimaryButton>
                <SecondaryButton onClick={() => navigate(`/generar-recibo/${appointment.id}`)}>
                  <Receipt />
                  Generar Recibo
                </SecondaryButton>
                <SuccessButton>
                  <CreditCard />
                  Pagar
                </SuccessButton>
                <SecondaryButton>
                  <Edit />
                  Reprogramar Cita
                </SecondaryButton>
                <DangerButton>
                  <X />
                  Cancelar Cita
                </DangerButton>
              </>
            )}
            {appointment.status === 'Completada' && (
              <SecondaryButton>
                <Printer />
                Reimprimir Ticket
              </SecondaryButton>
            )}
            {appointment.status === 'Cancelada' && (
              <SecondaryButton>
                <Calendar />
                Reagendar Cita
              </SecondaryButton>
            )}
          </ActionsContainer>
        </MainCard>
      </Content>
    </PageContainer>
  );
};

export default DetalleCita;
